import { useState, useCallback } from 'react';
import type { CharacterSheet, ClassEntry, SheetType, Ability, RestType, RepeatingSectionRow, SpellSlotsData, HitDiceData, TokenAction, ActiveEffect, SpellbookEntry } from '@roll1/shared';

export interface UseCharacterSheetsReturn {
  sheets: CharacterSheet[];
  selectedSheet: CharacterSheet | null;
  concurrentUpdateId: string | null;
  setSheets: React.Dispatch<React.SetStateAction<CharacterSheet[]>>;
  selectSheet: (id: string | null) => void;
  createSheet: (templateId: string, sheetType: SheetType, name: string, ownerName: string | null, values: Record<string, string | number | boolean | string[]>) => void;
  deleteSheet: (sheetId: string) => void;
  updateSheet: (sheetId: string, changes: Record<string, string | number | boolean | string[]>) => void;
  setPublicFields: (sheetId: string, publicFields: string[]) => void;
  bindToken: (sheetId: string, tokenId: string) => void;
  unbindToken: (sheetId: string) => void;
  applyDamage: (sheetId: string, amount: number) => void;
  applyHealing: (sheetId: string, amount: number) => void;
  abilityUse: (sheetId: string, abilityId: string) => void;
  abilityAdd: (sheetId: string, ability: { name: string; description: string; maxUses: number | null; restType: RestType }) => void;
  abilityRemove: (sheetId: string, abilityId: string) => void;
  rest: (sheetId: string, restType: 'short' | 'long') => void;
  addRepeatingRow: (sheetId: string, sectionKey: string, row: { rowId: string; values: Record<string, string | number | boolean | string[]> }) => void;
  removeRepeatingRow: (sheetId: string, sectionKey: string, rowId: string) => void;
  updateRepeatingRow: (sheetId: string, sectionKey: string, rowId: string, changes: Record<string, string | number | boolean | string[]>) => void;
  spendSpellSlot: (sheetId: string, level: number | 'pact') => void;
  // Server message handlers
  handleCreated: (sheet: CharacterSheet) => void;
  handleUpdated: (sheetId: string, changes: Record<string, string | number | boolean | string[]>, abilities?: Ability[]) => void;
  handleDeleted: (sheetId: string) => void;
  handleOwnerChanged: (sheetId: string, ownerName: string | null) => void;
  handlePublicFieldsUpdated: (sheetId: string, publicFields: string[], visibleValues: Record<string, string | number | boolean | string[]>) => void;
  handleTokenBound: (sheetId: string, tokenId: string) => void;
  handleTokenUnbound: (sheetId: string) => void;
  handleRepeatingRowAdded: (sheetId: string, sectionKey: string, row: RepeatingSectionRow) => void;
  handleRepeatingRowRemoved: (sheetId: string, sectionKey: string, rowId: string) => void;
  handleRepeatingRowUpdated: (sheetId: string, sectionKey: string, rowId: string, changes: Record<string, string | number | boolean | string[]>) => void;
  handleSpellSlotsUpdated: (sheetId: string, spellSlots: SpellSlotsData) => void;
  handleRestCompleted: (sheetId: string, restType: 'short' | 'long', spellSlots: SpellSlotsData | null, hp: number | null, hitDice?: { used: number } | null) => void;
  handleTokenActionsUpdated: (sheetId: string, tokenActions: TokenAction[]) => void;
  handlePortraitUpdated: (sheetId: string, portrait: string | null) => void;
  handleTokenDescriptionUpdated: (sheetId: string, tokenDescription: string) => void;
  handleHitDiceResult: (sheetId: string, newHp: number, hitDiceUsed: number) => void;
  handleEffectsUpdated: (sheetId: string, activeEffects: ActiveEffect[], values?: Record<string, string | number | boolean | string[]>) => void;
  handleSpellbookUpdated: (sheetId: string, spellbook: SpellbookEntry[]) => void;
  // RP inbound handlers — live path since the rp-echo mirroring was reinstated
  // (useMultiplayerSync routes sheet:rp-* echoes here). The adapter-mode HUD
  // derives rp from sheet.values.roleplay_json, so mutating the per-sheet store
  // is what makes RP edits appear live on other clients (GM + owner).
  handleRpFieldUpdated: (sheetId: string, field: string, value: string) => void;
  handleRpListChanged: (sheetId: string, listKey: string, op: 'add' | 'update' | 'remove', payload: { item?: Record<string, unknown>; itemId?: string; changes?: Record<string, unknown> }) => void;
  handleRpBoardConfigUpdated: (sheetId: string, config: Record<string, unknown>) => void;
  handleConcentrationUpdated: (sheetId: string, concentrationSpellId: string | null) => void;
  handleLevelUpComplete: (sheetId: string, values: Record<string, string | number | boolean | string[]>, abilities: Ability[], hitDice: HitDiceData | null, spellSlots: SpellSlotsData | null, spellbook: SpellbookEntry[], classData: ClassEntry[]) => void;
}

/** Apply a mutation to a sheet's freeform `values.roleplay_json` object,
 * returning a new sheet (immutable). roleplay_json is stored as a JSON object
 * despite the primitive `values` type — cast locally.
 * Identity guard: a no-op mutation (re-delivered echo, value already applied)
 * returns the SAME sheet reference — downstream treats a new sheet identity as
 * "authoritative state changed" (adaptedData recompute → pendingPatch
 * reconcile), so no-op echoes must not bump it. */
function applyRpMutation(sheet: CharacterSheet, mutate: (rp: Record<string, unknown>) => void): CharacterSheet {
  const values = { ...sheet.values } as Record<string, unknown>;
  const before = (values.roleplay_json && typeof values.roleplay_json === 'object' && !Array.isArray(values.roleplay_json))
    ? (values.roleplay_json as Record<string, unknown>)
    : {};
  const rp = { ...before };
  mutate(rp);
  if (JSON.stringify(rp) === JSON.stringify(before)) return sheet;
  values.roleplay_json = rp;
  return { ...sheet, values: values as CharacterSheet['values'] };
}

export function useCharacterSheets(): UseCharacterSheetsReturn {
  const [sheets, setSheets] = useState<CharacterSheet[]>([]);
  const [selectedSheetId, setSelectedSheetId] = useState<string | null>(null);
  const [concurrentUpdateId, setConcurrentUpdateId] = useState<string | null>(null);

  const selectedSheet = sheets.find(s => s.id === selectedSheetId) ?? null;

  const selectSheet = useCallback((id: string | null) => {
    setSelectedSheetId(id);
  }, []);

  // These are no-ops locally — actual mutations go through multiplayer send.
  // The server broadcast updates the local state via handle* callbacks.
  const createSheet = useCallback((_templateId: string, _sheetType: SheetType, _name: string, _ownerName: string | null, _values: Record<string, string | number | boolean | string[]>) => {
    // mp.send will be called by the wrapper in App.tsx
  }, []);

  const deleteSheet = useCallback((_sheetId: string) => {}, []);
  const updateSheet = useCallback((_sheetId: string, _changes: Record<string, string | number | boolean | string[]>) => {}, []);
  const setPublicFields = useCallback((_sheetId: string, _publicFields: string[]) => {
    // V007: Actual send happens through onSendSheet in SheetContext/PublicFieldsEditor
    // This hook-level stub is kept for API compatibility; UI uses onSendSheet directly.
  }, []);
  const bindToken = useCallback((_sheetId: string, _tokenId: string) => {}, []);
  const unbindToken = useCallback((_sheetId: string) => {}, []);
  const applyDamage = useCallback((_sheetId: string, _amount: number) => {}, []);
  const applyHealing = useCallback((_sheetId: string, _amount: number) => {}, []);
  const abilityUse = useCallback((_sheetId: string, _abilityId: string) => {}, []);
  const abilityAdd = useCallback((_sheetId: string, _ability: { name: string; description: string; maxUses: number | null; restType: RestType }) => {}, []);
  const abilityRemove = useCallback((_sheetId: string, _abilityId: string) => {}, []);
  const rest = useCallback((_sheetId: string, _restType: 'short' | 'long') => {}, []);
  const addRepeatingRow = useCallback((_sheetId: string, _sectionKey: string, _row: { rowId: string; values: Record<string, string | number | boolean | string[]> }) => {}, []);
  const removeRepeatingRow = useCallback((_sheetId: string, _sectionKey: string, _rowId: string) => {}, []);
  const updateRepeatingRow = useCallback((_sheetId: string, _sectionKey: string, _rowId: string, _changes: Record<string, string | number | boolean | string[]>) => {}, []);
  const spendSpellSlot = useCallback((_sheetId: string, _level: number | 'pact') => {}, []);

  // Server message handlers
  const handleCreated = useCallback((sheet: CharacterSheet) => {
    setSheets(prev => {
      if (prev.some(s => s.id === sheet.id)) return prev;
      return [...prev, sheet];
    });
  }, []);

  const handleUpdated = useCallback((sheetId: string, changes: Record<string, string | number | boolean | string[]>, abilities?: Ability[]) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      const topLevelKeys = ['activeEffects'] as const;
      // D&D 5e keys that belong in sheet.dnd5e namespace, not in sheet.values
      const dnd5eKeys = ['deathSaveState', 'concentrationSpellId', 'spellSlots', 'classData'] as const;
      const valueChanges: Record<string, string | number | boolean | string[]> = {};
      const topLevelChanges: Partial<CharacterSheet> = {};
      const dnd5eChanges: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(changes ?? {})) {
        if ((topLevelKeys as readonly string[]).includes(key)) {
          (topLevelChanges as Record<string, unknown>)[key] = val;
        } else if ((dnd5eKeys as readonly string[]).includes(key) && s.dnd5e) {
          dnd5eChanges[key] = val;
        } else {
          valueChanges[key] = val as string | number | boolean | string[];
        }
      }
      const updated: CharacterSheet = { ...s, ...topLevelChanges, values: { ...s.values, ...valueChanges } };
      if (Object.keys(dnd5eChanges).length > 0 && updated.dnd5e) {
        updated.dnd5e = { ...updated.dnd5e, ...dnd5eChanges } as typeof updated.dnd5e;
      }
      // portrait_visibility flows through sheet:updated (visibility-toggle
      // broadcast, see T004) as a flat value. It is not a topLevel/dnd5e key,
      // so it lands in valueChanges and merges into sheet.values here — the
      // portrait feed derives from sheet.values.portrait_visibility, and the
      // new sheet identity produced by this merge makes the feed pick up the
      // toggled value live on other clients without a reload. No dedicated
      // handler is required; extending this existing merge would duplicate it.
      // hit_dice_used flows through sheet:updated (hit-dice-restore broadcast) as
      // a flat value, but the adapter reads `used` from the structured
      // dnd5e.hitDice — keep it in sync so restoring hit dice reflects live.
      if ('hit_dice_used' in valueChanges && typeof valueChanges.hit_dice_used === 'number' && updated.dnd5e?.hitDice) {
        updated.dnd5e = { ...updated.dnd5e, hitDice: { ...updated.dnd5e.hitDice, used: valueChanges.hit_dice_used } };
      }
      if (abilities) updated.abilities = abilities;
      // sheet.name (canonical) vs values.name (a template FORM field that
      // happens to share the key) collide here: a rename sends changes.name
      // as the new sheet name, but the dnd5e subscriber broadcasts spread the
      // WHOLE values object (…sheet.values) into changes, dragging the empty
      // form-field values.name along — which would blank the displayed name on
      // every damage/death/concentration/rest event until reload (caught by
      // the death-state e2e). Guard: an empty string is never a real rename.
      if ('name' in valueChanges && typeof valueChanges.name === 'string' && valueChanges.name !== '') {
        updated.name = valueChanges.name;
      }
      return updated;
    }));
    // Brief concurrent-update notification
    setConcurrentUpdateId(sheetId);
    setTimeout(() => setConcurrentUpdateId(prev => prev === sheetId ? null : prev), 3000);
  }, []);

  const handleDeleted = useCallback((sheetId: string) => {
    setSheets(prev => prev.filter(s => s.id !== sheetId));
    setSelectedSheetId(prev => prev === sheetId ? null : prev);
  }, []);

  const handleOwnerChanged = useCallback((sheetId: string, ownerName: string | null) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      return { ...s, ownerName };
    }));
  }, []);

  const handlePublicFieldsUpdated = useCallback((sheetId: string, publicFields: string[], visibleValues: Record<string, string | number | boolean | string[]>) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      // REPLACE, not merge: the server sends the authoritative role-filtered
      // value set for this peer. A field the GM revoked (removed from
      // publicFields) is absent here and must disappear from the cache. Merging
      // (`{...s.values, ...visibleValues}`) could only ever add keys, so a
      // revoked field stayed visible to players forever.
      return { ...s, publicFields, values: visibleValues };
    }));
  }, []);

  const handleTokenBound = useCallback((_sheetId: string, _tokenId: string) => {
    // No local state change needed beyond what token:updated handles
  }, []);

  const handleTokenUnbound = useCallback((_sheetId: string) => {}, []);

  const handleRepeatingRowAdded = useCallback((sheetId: string, sectionKey: string, row: RepeatingSectionRow) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      const existing = s.repeatingData[sectionKey] ?? [];
      if (existing.some(r => r.rowId === row.rowId)) return s;
      return { ...s, repeatingData: { ...s.repeatingData, [sectionKey]: [...existing, row] } };
    }));
  }, []);

  const handleRepeatingRowRemoved = useCallback((sheetId: string, sectionKey: string, rowId: string) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      const existing = s.repeatingData[sectionKey];
      if (!existing) return s;
      return { ...s, repeatingData: { ...s.repeatingData, [sectionKey]: existing.filter(r => r.rowId !== rowId) } };
    }));
  }, []);

  const handleRepeatingRowUpdated = useCallback((sheetId: string, sectionKey: string, rowId: string, changes: Record<string, string | number | boolean | string[]>) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      const existing = s.repeatingData[sectionKey];
      if (!existing) return s;
      return {
        ...s,
        repeatingData: {
          ...s.repeatingData,
          [sectionKey]: existing.map(r =>
            r.rowId === rowId ? { ...r, values: { ...r.values, ...changes } } : r,
          ),
        },
      };
    }));
  }, []);

  const handleSpellSlotsUpdated = useCallback((sheetId: string, spellSlots: SpellSlotsData) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      return { ...s, dnd5e: s.dnd5e ? { ...s.dnd5e, spellSlots } : s.dnd5e };
    }));
  }, []);

  const handleRestCompleted = useCallback((sheetId: string, _restType: 'short' | 'long', spellSlots: SpellSlotsData | null, hp: number | null, hitDice?: { used: number } | null) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      const updated = { ...s };
      if (spellSlots && updated.dnd5e) updated.dnd5e = { ...updated.dnd5e, spellSlots };
      if (hp !== null) updated.values = { ...updated.values, hp };
      if (hitDice && updated.dnd5e?.hitDice) updated.dnd5e = { ...updated.dnd5e, hitDice: { ...updated.dnd5e.hitDice, used: hitDice.used } };
      return updated;
    }));
  }, []);

  const handleTokenActionsUpdated = useCallback((sheetId: string, tokenActions: TokenAction[]) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      return { ...s, tokenActions };
    }));
  }, []);

  const handlePortraitUpdated = useCallback((sheetId: string, portrait: string | null) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      return { ...s, portrait };
    }));
  }, []);

  const handleTokenDescriptionUpdated = useCallback((sheetId: string, tokenDescription: string) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      return { ...s, tokenDescription };
    }));
  }, []);

  const handleHitDiceResult = useCallback((sheetId: string, newHp: number, hitDiceUsed: number) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      // Update both the structured dnd5e.hitDice.used AND the flat
      // values.hit_dice_used. The HUD adapter reads `used` from dnd5e.hitDice
      // when present, else from values.hit_dice_used (legacy/null-struct sheets)
      // — keeping both in sync makes the spent count move live in either case.
      return {
        ...s,
        values: { ...s.values, hp: newHp, hit_dice_used: hitDiceUsed },
        dnd5e: s.dnd5e?.hitDice ? { ...s.dnd5e, hitDice: { ...s.dnd5e.hitDice, used: hitDiceUsed } } : s.dnd5e,
      };
    }));
  }, []);

  const handleEffectsUpdated = useCallback((sheetId: string, activeEffects: ActiveEffect[], values?: Record<string, string | number | boolean | string[]>) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      // Merge the server-recomputed `values` (AC/saves raised by Bless/Shield
      // etc.) so the derived stats move live — previously only `activeEffects`
      // was applied and the numeric effect stayed until reload.
      return { ...s, activeEffects, ...(values ? { values: { ...s.values, ...values } } : {}) };
    }));
  }, []);

  const handleSpellbookUpdated = useCallback((sheetId: string, spellbook: SpellbookEntry[]) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      return { ...s, dnd5e: s.dnd5e ? { ...s.dnd5e, spellbook } : s.dnd5e };
    }));
  }, []);

  const handleRpFieldUpdated = useCallback((sheetId: string, field: string, value: string) => {
    setSheets(prev => prev.map(s => s.id === sheetId ? applyRpMutation(s, rp => { rp[field] = value; }) : s));
  }, []);

  const handleRpListChanged = useCallback((sheetId: string, listKey: string, op: 'add' | 'update' | 'remove', payload: { item?: Record<string, unknown>; itemId?: string; changes?: Record<string, unknown> }) => {
    setSheets(prev => prev.map(s => s.id === sheetId ? applyRpMutation(s, rp => {
      const list = Array.isArray(rp[listKey]) ? [...(rp[listKey] as Array<Record<string, unknown> & { id?: string }>)] : [];
      if (op === 'add' && payload.item) {
        rp[listKey] = [...list, payload.item];
      } else if (op === 'update' && payload.itemId) {
        rp[listKey] = list.map(i => i.id === payload.itemId ? { ...i, ...payload.changes, id: payload.itemId } : i);
      } else if (op === 'remove' && payload.itemId) {
        rp[listKey] = list.filter(i => i.id !== payload.itemId);
      }
    }) : s));
  }, []);

  const handleRpBoardConfigUpdated = useCallback((sheetId: string, config: Record<string, unknown>) => {
    setSheets(prev => prev.map(s => s.id === sheetId ? applyRpMutation(s, rp => { rp.boardConfig = config; }) : s));
  }, []);

  const handleConcentrationUpdated = useCallback((sheetId: string, concentrationSpellId: string | null) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      return { ...s, dnd5e: s.dnd5e ? { ...s.dnd5e, concentrationSpellId } : s.dnd5e };
    }));
  }, []);

  const handleLevelUpComplete = useCallback((sheetId: string, values: Record<string, string | number | boolean | string[]>, abilities: Ability[], hitDice: HitDiceData | null, spellSlots: SpellSlotsData | null, spellbook: SpellbookEntry[], classData: ClassEntry[]) => {
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      // spellbook included so newly-learned spells appear live — previously the
      // broadcast omitted it, so known spells only showed after a page reload.
      return { ...s, values: { ...s.values, ...values }, abilities, dnd5e: s.dnd5e ? { ...s.dnd5e, hitDice, spellSlots, spellbook, classData } : s.dnd5e };
    }));
  }, []);

  return {
    sheets,
    selectedSheet,
    concurrentUpdateId,
    setSheets,
    selectSheet,
    createSheet,
    deleteSheet,
    updateSheet,
    setPublicFields,
    bindToken,
    unbindToken,
    applyDamage,
    applyHealing,
    abilityUse,
    abilityAdd,
    abilityRemove,
    rest,
    addRepeatingRow,
    removeRepeatingRow,
    updateRepeatingRow,
    spendSpellSlot,
    handleCreated,
    handleUpdated,
    handleDeleted,
    handleOwnerChanged,
    handlePublicFieldsUpdated,
    handleTokenBound,
    handleTokenUnbound,
    handleRepeatingRowAdded,
    handleRepeatingRowRemoved,
    handleRepeatingRowUpdated,
    handleSpellSlotsUpdated,
    handleRestCompleted,
    handleTokenActionsUpdated,
    handlePortraitUpdated,
    handleTokenDescriptionUpdated,
    handleHitDiceResult,
    handleEffectsUpdated,
    handleSpellbookUpdated,
    handleRpFieldUpdated,
    handleRpListChanged,
    handleRpBoardConfigUpdated,
    handleConcentrationUpdated,
    handleLevelUpComplete,
  };
}
